import { STAGE_TYPES, STATUS_TYPES } from 'core/constants/jobCardConstant';
import {
  IApplicationHistoryActor,
  IApplicationHistoryEntry
} from 'core/interface/applicationHistory';
import { getDateFormatter } from 'helpers/dateConverter';

const toNumber = (value: number | string | null | undefined): number | null => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const numericValue = Number(value);
  return Number.isNaN(numericValue) ? null : numericValue;
};

const getStageLabel = (stage: number | string | null | undefined): string => {
  const numericStage = toNumber(stage);

  if (numericStage === null) {
    return String(stage);
  }

  return (
    STAGE_TYPES.find((item) => item.value === numericStage)?.to_show ??
    String(stage)
  );
};

const getStatusLabel = (status: number | string | null | undefined): string => {
  const numericStatus = toNumber(status);

  if (numericStatus === null) {
    return String(status);
  }

  return (
    STATUS_TYPES.find((item) => item.value === numericStatus)?.label ??
    String(status)
  );
};

const getPersonName = (
  person: IApplicationHistoryActor | string | number | null | undefined
): string | null => {
  if (person === null || person === undefined) {
    return null;
  }

  if (typeof person === 'string') {
    const trimmed = person.trim();
    return trimmed || null;
  }

  if (typeof person === 'number') {
    return null;
  }

  const fullName = [person.first_name, person.last_name]
    .filter(Boolean)
    .join(' ')
    .trim();

  return person.name?.trim() || fullName || person.email?.trim() || null;
};

export const getHistoryRecruiterName = (
  entry: IApplicationHistoryEntry
): string | null => getPersonName(entry.recruiter);

export const formatTimestampedLine = (
  label: string,
  timestamp: string,
  t: (key: string) => string,
  options?: { includeOn?: boolean; recruiterName?: string | null }
): string => {
  const formattedDate = getDateFormatter(timestamp, 'MMM DD YYYY');
  const includeOn = options?.includeOn ?? true;
  const line = includeOn
    ? `${t(label)} ${t('on')} ${formattedDate}`
    : `${t(label)} ${formattedDate}`;
  const recruiterName = options?.recruiterName?.trim();

  return recruiterName ? `${line} ${t('by')} ${recruiterName}` : line;
};

export const normalizeApplicationHistory = (
  history: IApplicationHistoryEntry[] | null | undefined
): IApplicationHistoryEntry[] => {
  return Array.isArray(history) ? history : [];
};

const shouldPreferStatus = (entry: IApplicationHistoryEntry): boolean => {
  const numericStatus = toNumber(entry.status);

  return numericStatus !== null && numericStatus >= 2;
};

const getHistoryEntryLabel = (entry: IApplicationHistoryEntry): string | null => {
  if (shouldPreferStatus(entry)) {
    return getStatusLabel(entry.status);
  }

  const numericStage = toNumber(entry.stage);

  if (numericStage !== null) {
    return getStageLabel(entry.stage);
  }

  const numericStatus = toNumber(entry.status);

  if (numericStatus !== null) {
    return getStatusLabel(entry.status);
  }

  return null;
};

export const formatApplicationHistoryEntry = (
  entry: IApplicationHistoryEntry,
  t: (key: string) => string
): string => {
  const label = getHistoryEntryLabel(entry);

  if (!label) {
    return getDateFormatter(entry.timestamp, 'MMM DD YYYY');
  }

  return formatTimestampedLine(label, entry.timestamp, t, {
    recruiterName: getHistoryRecruiterName(entry)
  });
};

export const sortApplicationHistory = (
  history: IApplicationHistoryEntry[] | null | undefined = []
): IApplicationHistoryEntry[] => {
  return normalizeApplicationHistory(history).sort(
    (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
  );
};

export const mergeApplicationUpdate = (
  existing: Record<string, unknown>,
  update: Record<string, unknown>
): Record<string, unknown> => {
  const merged = { ...existing, ...update };

  if (!Array.isArray(update.history) && Array.isArray(existing.history)) {
    merged.history = existing.history;
  }

  return merged;
};
